import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/shipping-delivery")({
  head: () => ({
    meta: [
      { title: "Shipping & Delivery Policy - WeLivdIt" },
      {
        name: "description",
        content:
          "WeLivdIt provides digital visa coaching services. There are no physical shipping requirements - services are delivered digitally.",
      },
      { property: "og:title", content: "Shipping & Delivery Policy - WeLivdIt" },
      { property: "og:description", content: "Digital service delivery policy for WeLivdIt visa coaching." },
      { property: "og:url", content: "https://welivdit.com/shipping-delivery" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/shipping-delivery" }],
  }),
  component: ShippingDeliveryPage,
});

function ShippingDeliveryPage() {
  return (
    <section className="mx-auto max-w-3xl px-5 py-16 md:py-24">
      <p className="text-sm font-medium text-primary uppercase tracking-wider">Legal</p>
      <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight">
        Shipping &amp; Delivery Policy
      </h1>
      <p className="mt-3 text-sm text-muted-foreground">
        Last updated: {new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}
      </p>

      <div className="mt-10 space-y-6 text-foreground/80 leading-relaxed">
        <p className="rounded-2xl border border-border bg-card p-5 font-medium text-foreground">
          We deliver our services digitally via video conferencing tools (Zoom / Google Meet) and email. No physical
          shipping is involved.
        </p>
        <p>
          WeLivdIt provides digital services only. There are no physical shipping requirements, no couriered materials,
          and no printed workbooks. Everything you purchase is delivered online - over live video coaching calls, over
          email, and over WhatsApp - so applicants in any city or time zone receive exactly the same service.
        </p>
        <p>
          The moment your payment succeeds, an automated booking confirmation is issued to the email address and mobile
          number you entered at checkout. That confirmation contains your plan name, what is included, the number of
          one-on-one mock interviews you are entitled to, and a secure link to your student dashboard. Alongside it you
          receive a Calendly scheduling link, which lets you pick your own session slots against our coaches' live
          availability rather than waiting for a manual reply. Slots are released in Asia/Kolkata time and can be
          rescheduled from the same link if your visa interview date moves.
        </p>
        <p>
          Before your first session we send a short intake form covering your DS-160 details, university and programme,
          funding structure, and interview date. Completing this form within 24 hours of purchase is what allows your
          coach to review your profile in advance, so the first mock interview starts with diagnosis rather than
          data-collection. If the intake form is not completed, sessions can still go ahead, but the first 10 minutes
          are usually spent gathering the same information.
        </p>
        <p>
          Session joining links (Google Meet or Zoom) are shared over email and WhatsApp shortly before each scheduled
          slot. DS-160 reviews and written feedback are returned over email or WhatsApp within the turnaround time
          stated on your plan page. In practice, the entire delivery chain - confirmation, scheduling, intake, first
          session - completes within a few days of purchase.
        </p>


        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">How services are delivered</h2>
          <ul className="mt-2 list-disc pl-5 space-y-1">
            <li>
              <strong>Mock interviews & coaching sessions:</strong> Scheduled over a video call (Google Meet / Zoom) at
              a mutually agreed time after purchase.
            </li>
            <li>
              <strong>DS-160 reviews & documentation feedback:</strong> Delivered over email or WhatsApp within the
              agreed turnaround time.
            </li>
            <li>
              <strong>Important:</strong> We do not share documents, question banks, sample answer sheets, or written
              study materials. All coaching is delivered live during your scheduled sessions.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">Delivery timelines</h2>
          <p className="mt-2">
            Confirmation of purchase is sent immediately to your registered email and WhatsApp number. Session
            scheduling is typically completed within 24–48 hours of payment.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">Need help?</h2>
          <p className="mt-2">
            For any delivery-related queries, contact us at{" "}
            <a href="mailto:hello@welivdit.com" className="text-primary underline">
              hello@welivdit.com
            </a>{" "}
            or call{" "}
            <a href="tel:+916300736712" className="text-primary underline">
              +91 63007 36712
            </a>
            .
          </p>
        </section>
      </div>
    </section>
  );
}
